import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import timeGridPlugin from "@fullcalendar/timegrid";
import timelinePlugin from "@fullcalendar/timeline";
import {
  Box,
  Breadcrumbs,
  Button,
  Card,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Link,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useEffect, useRef, useState, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { Link as RouterLink } from "react-router-dom";
import toast from "react-hot-toast";
import ChevronRightIcon from "../../../icons/ChevronRight";
import { CalendarToolbar } from "../../../components/dashboard/calendar";
import moment from "moment";
import FullCalendarWrapper from "./_components/FullCalendarWrapper";
import FilterDropdown, { FILTER_TYPES } from "./_components/FilterDropdown";
import EventFormDialog from "./_components/EventFormDialog";
import CustomRecurrenceDialog from "./_components/CustomRecurrenceDialog";
import { colorOptions } from "./_components/ColorSelector";
import {
  useCreateCalendarItem,
  useFetchCalendarEvents,
  useUpdateCalendarItem,
  useDeleteCalendarItem,
} from "../../../hooks/useCalendarEvents";
import { getUsersUnderAdmin } from "../../../api/project/Project";
import {
  transformEventFormToApi,
  transformTaskFormToApi,
  transformReminderFormToApi,
  CALENDAR_ITEM_TYPES,
  transformApiToCalendarEvent,
} from "../../../utils/calendarTransformers";

// Constants for update/delete scope values
const UPDATE_SCOPE = {
  THIS_ONLY: 1,
  THIS_AND_FOLLOWING: 2,
  ALL: 3,
};

// Constants for default colors
const DEFAULT_COLORS = {
  TASK: "#33FF57",
  REMINDER: "#FFC300",
  EVENT: "#1e88e5",
};

const CalendarEvents = () => {
  const calendarRef = useRef(null);
  const mobileDevice = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  // MEDIUM PRIORITY FIX #10: Standardize on moment for date handling
  const [date, setDate] = useState(moment().toDate());
  const [view, setView] = useState(mobileDevice ? "listWeek" : "dayGridMonth");

  // Dialog state for creating a new calendar item (event/task/reminder).
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTab, setDialogTab] = useState("event");
  const [dialogTitle, setDialogTitle] = useState("");

  // Editing state – when set, dialog works in "edit" mode for an existing item
  const [editingItemId, setEditingItemId] = useState(null);

  // Event form state
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [eventStartTime, setEventStartTime] = useState(null);
  const [eventEndTime, setEventEndTime] = useState(null);
  const [isEventAllDay, setIsEventAllDay] = useState(false);
  const [eventRepeat, setEventRepeat] = useState("none");
  const [location, setLocation] = useState("");
  const [description, setDescription] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);

  // Task form state
  const [taskStartTime, setTaskStartTime] = useState(null);
  const [taskEndTime, setTaskEndTime] = useState(null);
  const [isTaskAllDay, setIsTaskAllDay] = useState(true);
  const [taskSelectedUsers, setTaskSelectedUsers] = useState([]);
  const [taskSelectedColor, setTaskSelectedColor] = useState(DEFAULT_COLORS.TASK);
  const [taskColorAnchorEl, setTaskColorAnchorEl] = useState(null);

  // Reminder form state
  const [reminderDate, setReminderDate] = useState(null);
  const [reminderStartTime, setReminderStartTime] = useState(null);
  const [reminderEndTime, setReminderEndTime] = useState(null);
  const [isReminderAllDay, setIsReminderAllDay] = useState(true);
  const [reminderSelectedUsers, setReminderSelectedUsers] = useState([]);
  const [reminderSelectedColor, setReminderSelectedColor] = useState(
    DEFAULT_COLORS.REMINDER
  );
  const [reminderColorAnchorEl, setReminderColorAnchorEl] = useState(null);

  // Filter state
  const [selectedFilterTypes, setSelectedFilterTypes] = useState(FILTER_TYPES);

  // Color selector state
  const [selectedColor, setSelectedColor] = useState(colorOptions[0]);
  const [colorAnchorEl, setColorAnchorEl] = useState(null);

  // Notification state
  const [notifications, setNotifications] = useState([]);

  // Custom recurrence dialog state
  const [customRecurrenceOpen, setCustomRecurrenceOpen] = useState(false);
  const [customRepeatEvery, setCustomRepeatEvery] = useState(1);
  const [customRepeatUnit, setCustomRepeatUnit] = useState("month");
  const [customMonthlyPattern, setCustomMonthlyPattern] =
    useState("day-of-month");
  const [customWeeklyDays, setCustomWeeklyDays] = useState([]);
  const [customEndMode, setCustomEndMode] = useState("on");
  const [customEndOnDate, setCustomEndOnDate] = useState(
    moment().add(1, "year")
  );
  const [customEndAfterCount, setCustomEndAfterCount] = useState(12);

  // API hooks
  const { createItem, loading: createLoading } = useCreateCalendarItem();
  const { updateItem, loading: updateLoading } = useUpdateCalendarItem();
  const { deleteItem, loading: deleteLoading } = useDeleteCalendarItem();
  const { events: apiEvents, refetch: refetchEvents } =
    useFetchCalendarEvents();

  // LOW PRIORITY FIX #16: User list for mapping user_ids when editing events
  const [userList, setUserList] = useState([]);

  // Fetch users for mapping when editing events
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await getUsersUnderAdmin();
        if (res?.data?.success) {
          setUserList(res.data.data.peopleDropdown || []);
        }
      } catch (error) {
        if (process.env.NODE_ENV === "development") {
          console.error("Error fetching users for mapping:", error);
        }
      }
    };
    fetchUsers();
  }, []);

  // Delete confirmation dialog state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteScope, setDeleteScope] = useState(UPDATE_SCOPE.THIS_ONLY);
  const [eventToDelete, setEventToDelete] = useState(null); // Store event data to check if recurring

  // Update scope state for recurring events
  const [updateScope, setUpdateScope] = useState(UPDATE_SCOPE.THIS_ONLY);

  // Map API events into FullCalendar format using shared transformer
  // CRITICAL FIX #1: Apply filter based on selectedFilterTypes
  const calendarEvents = useMemo(() => {
    if (!Array.isArray(apiEvents)) return [];

    // Map calendar item type numbers to filter type strings
    const typeToFilterMap = {
      [CALENDAR_ITEM_TYPES.EVENT]: "Event",
      [CALENDAR_ITEM_TYPES.TASK]: "Task",
      [CALENDAR_ITEM_TYPES.REMINDER]: "Reminder",
    };

    const filtered = apiEvents
      .filter((event) => {
        const type = event.type || CALENDAR_ITEM_TYPES.EVENT;
        const filterType = typeToFilterMap[type];
        // If filter type exists in map, check if it's selected
        // Otherwise, include it (for other types like Holiday, etc.)
        return filterType
          ? selectedFilterTypes.includes(filterType)
          : true; // Include unknown types by default
      })
      .map((event) => transformApiToCalendarEvent(event));

    return filtered;
  }, [apiEvents, selectedFilterTypes]);

  // CRITICAL FIX #2: Calculate date range based on current calendar view
  const dateRange = useMemo(() => {
    const calendarEl = calendarRef.current;
    if (!calendarEl) {
      // Fallback to month range if calendar not yet initialized
      const start = moment(date).startOf("month").format("YYYY-MM-DD");
      const end = moment(date).endOf("month").format("YYYY-MM-DD");
      return { startDate: start, endDate: end };
    }

    try {
      const calendarApi = calendarEl.getApi();
      const view = calendarApi.view;
      if (view && view.activeStart && view.activeEnd) {
        const start = moment(view.activeStart).format("YYYY-MM-DD");
        const end = moment(view.activeEnd).format("YYYY-MM-DD");
        return { startDate: start, endDate: end };
      }
    } catch (error) {
      // Fallback if calendar API not ready
      if (process.env.NODE_ENV === "development") {
        console.warn("Calendar API not ready, using month range:", error);
      }
    }

    // Fallback to month range
    const start = moment(date).startOf("month").format("YYYY-MM-DD");
    const end = moment(date).endOf("month").format("YYYY-MM-DD");
    return { startDate: start, endDate: end };
  }, [date, view]);

  // CRITICAL FIX #3: Fetch events for the current visible range
  // MEDIUM PRIORITY FIX #11: Add cleanup for async operations
  // refetchEvents is memoized in the hook with useCallback, so it's safe to include
  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      const result = await refetchEvents(dateRange);
      // Prevent state updates if component unmounted
      if (cancelled) {
        return;
      }
      // Result handling is done in the hook, no additional handling needed here
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [dateRange, refetchEvents]);

  // CRITICAL FIX #2 (Additional): Listen for calendar view changes to sync date range
  useEffect(() => {
    const calendarEl = calendarRef.current;
    if (!calendarEl) return;

    const calendarApi = calendarEl.getApi();
    if (!calendarApi) return;

    const handleDatesSet = () => {
      try {
        const view = calendarApi.view;
        if (view && view.activeStart && view.activeEnd) {
          const start = moment(view.activeStart).format("YYYY-MM-DD");
          const end = moment(view.activeEnd).format("YYYY-MM-DD");
          refetchEvents({ startDate: start, endDate: end });
        }
      } catch (error) {
        if (process.env.NODE_ENV === "development") {
          console.warn("Error handling datesSet event:", error);
        }
      }
    };

    calendarApi.on("datesSet", handleDatesSet);
    return () => {
      try {
        calendarApi.off("datesSet", handleDatesSet);
      } catch (error) {
        // Ignore cleanup errors
      }
    };
  }, [view, refetchEvents]); // Re-setup listener when view changes

  // Calendar navigation handlers
  const handleDateToday = () => {
    const calendarEl = calendarRef.current;
    if (calendarEl) {
      const calendarApi = calendarEl.getApi();
      calendarApi.today();
      setDate(calendarApi.getDate());
    }
  };

  const handleViewChange = (newView) => {
    const calendarEl = calendarRef.current;
    if (calendarEl) {
      const calendarApi = calendarEl.getApi();
      calendarApi.changeView(newView);
      setView(newView);

      // When view changes, also update the date to the calendar's current date
      setDate(calendarApi.getDate());
    }
  };

  const handleDatePrev = () => {
    const calendarEl = calendarRef.current;
    if (calendarEl) {
      const calendarApi = calendarEl.getApi();
      calendarApi.prev();
      setDate(calendarApi.getDate());
    }
  };

  const handleDateNext = () => {
    const calendarEl = calendarRef.current;
    if (calendarEl) {
      const calendarApi = calendarEl.getApi();
      calendarApi.next();
      setDate(calendarApi.getDate());
    }
  };

  const handleDateClick = (arg) => {
    const baseMoment = moment(arg.date);
    setStartDate(baseMoment);
    setEndDate(baseMoment);
    setReminderDate(baseMoment);
    setDialogTab("event");
    setDialogTitle("");
    setLocation("");
    setDescription("");
    setSelectedUsers([]);
    setEditingItemId(null);
    setEventToDelete(null);
    setDialogOpen(true);
  };

  const handleDialogTabChange = (_event, value) => {
    setDialogTab(value);
  };

  // When clicking an existing calendar item, open dialog in edit mode with prefilled values
  const handleEventClick = (clickInfo) => {
    const { event } = clickInfo;
    const extended = event.extendedProps || {};
    const type = extended.type || CALENDAR_ITEM_TYPES.EVENT;

    // Use backend id from extended props when available
    setEditingItemId(extended.id || event.id);
    setDialogTitle(event.title || "");
    setDescription(extended.description || extended.note || "");

    // Common: event.start / event.end are full Date objects with time
    const startMoment = event.start ? moment(event.start) : moment();
    const endMoment = event.end ? moment(event.end) : startMoment.clone();
    const onlyDay = Boolean(extended.only_day ?? event.allDay);

    // Store event data for delete scope detection
    setEventToDelete(extended);

    // Handle based on type
    if (type === CALENDAR_ITEM_TYPES.EVENT) {
      setDialogTab("event");
      setLocation(extended.location || "");

      // Date pickers
      setStartDate(startMoment);
      setEndDate(endMoment);
      setIsEventAllDay(onlyDay);

      // Time pickers
      if (!onlyDay) {
        setEventStartTime(startMoment);
        setEventEndTime(endMoment);
      } else {
        setEventStartTime(null);
        setEventEndTime(null);
      }

      // Color
      if (extended.color) {
        setSelectedColor(extended.color);
      }

      // LOW PRIORITY FIX #16: Map user_ids to user objects for events
      if (extended.user_ids && Array.isArray(extended.user_ids) && userList.length > 0) {
        const mappedUsers = extended.user_ids
          .map((id) => userList.find((u) => u.id === id))
          .filter(Boolean);
        setSelectedUsers(mappedUsers);
      }
    } else if (type === CALENDAR_ITEM_TYPES.TASK) {
      setDialogTab("task");

      // Due date (task uses startDate for due date)
      setStartDate(startMoment);
      setIsTaskAllDay(onlyDay);

      // Time pickers for task
      if (!onlyDay) {
        setTaskStartTime(startMoment);
        setTaskEndTime(endMoment);
      } else {
        setTaskStartTime(null);
        setTaskEndTime(null);
      }

      // Color
      if (extended.color) {
        setTaskSelectedColor(extended.color);
      }

      // LOW PRIORITY FIX #16: Map user_ids to user objects for tasks
      if (extended.user_ids && Array.isArray(extended.user_ids) && userList.length > 0) {
        const mappedUsers = extended.user_ids
          .map((id) => userList.find((u) => u.id === id))
          .filter(Boolean);
        setTaskSelectedUsers(mappedUsers);
      }
    } else if (type === CALENDAR_ITEM_TYPES.REMINDER) {
      setDialogTab("reminder");

      // Reminder date
      setReminderDate(startMoment);
      setIsReminderAllDay(onlyDay);

      // Time pickers for reminder
      if (!onlyDay) {
        setReminderStartTime(startMoment);
        setReminderEndTime(endMoment);
      } else {
        setReminderStartTime(null);
        setReminderEndTime(null);
      }

      // Color
      if (extended.color) {
        setReminderSelectedColor(extended.color);
      }

      // LOW PRIORITY FIX #16: Map user_ids to user objects for reminders
      if (extended.user_ids && Array.isArray(extended.user_ids) && userList.length > 0) {
        const mappedUsers = extended.user_ids
          .map((id) => userList.find((u) => u.id === id))
          .filter(Boolean);
        setReminderSelectedUsers(mappedUsers);
      }
    }

    setDialogOpen(true);
  };

  // Filter handlers
  const handleFilterChange = (event) => {
    const {
      target: { value },
    } = event;
    setSelectedFilterTypes(
      typeof value === "string" ? value.split(",") : value
    );
  };

  const handleFilterSelectAll = () => {
    setSelectedFilterTypes(FILTER_TYPES);
  };

  const handleFilterDeselectAll = () => {
    setSelectedFilterTypes([]);
  };

  // Color selector handlers
  const handleColorMenuOpen = (event) => {
    setColorAnchorEl(event.currentTarget);
  };

  const handleColorMenuClose = () => {
    setColorAnchorEl(null);
  };

  const handleColorSelect = (color) => {
    setSelectedColor(color);
    setColorAnchorEl(null);
  };

  // Notification handlers
  const handleAddNotification = () => {
    setNotifications((prev) => [
      ...prev,
      {
        id: Date.now(),
        amount: 1,
        unit: "Hours",
        time: moment(),
      },
    ]);
  };

  const handleNotificationChange = (id, key, value) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, [key]: value } : n))
    );
  };

  const handleRemoveNotification = (id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  // LOW PRIORITY FIX #14: Extract duplicate reset logic to a function
  const resetAllFormState = () => {
    setDialogTitle("");
    setLocation("");
    setDescription("");
    setSelectedUsers([]);
    setStartDate(null);
    setEndDate(null);
    setEventStartTime(null);
    setEventEndTime(null);
    setIsEventAllDay(false);
    setEventRepeat("none");
    setNotifications([]);
    // Reset task state
    setTaskStartTime(null);
    setTaskEndTime(null);
    setIsTaskAllDay(true);
    setTaskSelectedUsers([]);
    setTaskSelectedColor(DEFAULT_COLORS.TASK);
    // Reset reminder state
    setReminderDate(null);
    setReminderStartTime(null);
    setReminderEndTime(null);
    setIsReminderAllDay(true);
    setReminderSelectedUsers([]);
    setReminderSelectedColor(DEFAULT_COLORS.REMINDER);
    setDialogOpen(false);
    setEditingItemId(null);
    setUpdateScope(UPDATE_SCOPE.THIS_ONLY);
  };

  // Event form handlers
  const handleStartDateChange = (value) => {
    setStartDate(value);
    if (value && endDate && value.isAfter(endDate)) {
      setEndDate(value);
    }
  };

  const handleAllDayChange = (e) => {
    const checked = e.target.checked;
    setIsEventAllDay(checked);
    if (checked) {
      setEventStartTime(null);
      setEventEndTime(null);
    }
  };

  const handleReminderAllDayChange = (e) => {
    const checked = e.target.checked;
    setIsReminderAllDay(checked);
    if (checked) {
      setReminderStartTime(null);
      setReminderEndTime(null);
    }
  };

  // Reminder color handlers
  const handleReminderColorMenuOpen = (event) => {
    setReminderColorAnchorEl(event.currentTarget);
  };

  const handleReminderColorMenuClose = () => {
    setReminderColorAnchorEl(null);
  };

  const handleReminderColorSelect = (color) => {
    setReminderSelectedColor(color);
    setReminderColorAnchorEl(null);
  };

  // Task form handlers
  const handleTaskAllDayChange = (e) => {
    const checked = e.target.checked;
    setIsTaskAllDay(checked);
    if (checked) {
      setTaskStartTime(null);
      setTaskEndTime(null);
    }
  };

  const handleTaskColorMenuOpen = (event) => {
    setTaskColorAnchorEl(event.currentTarget);
  };

  const handleTaskColorMenuClose = () => {
    setTaskColorAnchorEl(null);
  };

  const handleTaskColorSelect = (color) => {
    setTaskSelectedColor(color);
    setTaskColorAnchorEl(null);
  };

  // Form submission handler
  const handleSave = async () => {
    // HIGH PRIORITY FIX #7: Race condition protection
    if (createLoading || updateLoading) {
      return; // Prevent double submission
    }

    // HIGH PRIORITY FIX #8: Comprehensive validation
    if (!dialogTitle.trim()) {
      toast.error("Title is required");
      return;
    }

    // Validate dates based on dialog tab
    if (dialogTab === "event") {
      if (!startDate || !endDate) {
        toast.error("Start date and end date are required");
        return;
      }
      if (moment(startDate).isAfter(moment(endDate))) {
        toast.error("Start date cannot be after end date");
        return;
      }
      if (!isEventAllDay && (!eventStartTime || !eventEndTime)) {
        toast.error("Start time and end time are required for timed events");
        return;
      }
    } else if (dialogTab === "task") {
      if (!startDate) {
        toast.error("Due date is required");
        return;
      }
      if (!isTaskAllDay && (!taskStartTime || !taskEndTime)) {
        toast.error("Start time and end time are required for timed tasks");
        return;
      }
    } else if (dialogTab === "reminder") {
      if (!reminderDate) {
        toast.error("Reminder date is required");
        return;
      }
      if (!isReminderAllDay && (!reminderStartTime || !reminderEndTime)) {
        toast.error("Start time and end time are required for timed reminders");
        return;
      }
    }

    try {
      let payload;
      let itemType;

      if (dialogTab === "event") {
        itemType = CALENDAR_ITEM_TYPES.EVENT;
        const userIds = selectedUsers.map((user) => user.id);
        payload = transformEventFormToApi(
          {
            startDate,
            endDate,
            eventStartTime,
            eventEndTime,
            isEventAllDay,
            eventRepeat,
            selectedColor,
            notifications,
            // Custom recurrence fields
            customRepeatEvery,
            customRepeatUnit,
            customMonthlyPattern,
            customWeeklyDays,
            customEndMode,
            customEndOnDate,
            customEndAfterCount,
          },
          userIds,
          dialogTitle,
          location,
          description
        );
      } else if (dialogTab === "task") {
        itemType = CALENDAR_ITEM_TYPES.TASK;
        const taskUserIds = taskSelectedUsers.map((user) => user.id);
        payload = transformTaskFormToApi(
          {
            dueDate: startDate,
            startTime: taskStartTime,
            endTime: taskEndTime,
            isAllDay: isTaskAllDay,
            selectedColor: taskSelectedColor,
          },
          taskUserIds,
          dialogTitle,
          description
        );
      } else if (dialogTab === "reminder") {
        itemType = CALENDAR_ITEM_TYPES.REMINDER;
        const reminderUserIds = reminderSelectedUsers.map((user) => user.id);
        payload = transformReminderFormToApi(
          {
            reminderDate,
            startTime: reminderStartTime,
            endTime: reminderEndTime,
            isAllDay: isReminderAllDay,
            selectedColor: reminderSelectedColor,
          },
          reminderUserIds,
          dialogTitle,
          description
        );
      }

      let result;

      // If we're editing an existing calendar item, call the update hook
      if (editingItemId) {
        // Build update payload with event_id, type, and update_on
        // Exclude date and repeat fields when editing (they cannot be updated)
        const { start_date, end_date, start_time, end_time, only_day, repeat, repeat_times, repeat_duration_type, month_on, repeat_weekly_days, end_type, ends_on, ...updateableFields } = payload;
        const updatePayload = {
          event_id: editingItemId,
          type: itemType,
          update_on: updateScope,
          ...updateableFields,
        };
        result = await updateItem(updatePayload);
      } else {
        result = await createItem(itemType, payload);
      }

      if (result.success) {
        // LOW PRIORITY FIX #14: Use extracted reset function
        resetAllFormState();

        // Refresh calendar events for the current visible range
        const calendarEl = calendarRef.current;
        if (calendarEl) {
          try {
            const calendarApi = calendarEl.getApi();
            const view = calendarApi.view;
            if (view && view.activeStart && view.activeEnd) {
              const start = moment(view.activeStart).format("YYYY-MM-DD");
              const end = moment(view.activeEnd).format("YYYY-MM-DD");
              refetchEvents({ startDate: start, endDate: end });
            } else {
              // Fallback to month range
              const start = moment(date).startOf("month").format("YYYY-MM-DD");
              const end = moment(date).endOf("month").format("YYYY-MM-DD");
              refetchEvents({ startDate: start, endDate: end });
            }
          } catch (err) {
            // Fallback to month range if calendar API fails
            const start = moment(date).startOf("month").format("YYYY-MM-DD");
            const end = moment(date).endOf("month").format("YYYY-MM-DD");
            refetchEvents({ startDate: start, endDate: end });
          }
        } else {
          // Fallback to month range
          const start = moment(date).startOf("month").format("YYYY-MM-DD");
          const end = moment(date).endOf("month").format("YYYY-MM-DD");
          refetchEvents({ startDate: start, endDate: end });
        }
      } else {
        // CRITICAL FIX #4: Handle failure case - hooks show toast, but ensure we don't proceed
        // Error message already shown by hook via toast
        if (process.env.NODE_ENV === "development") {
          console.error("Failed to save calendar item:", result.error);
        }
      }
    } catch (error) {
      // CRITICAL FIX #4: Ensure errors are properly handled
      // Hooks handle toast messages, but log for debugging
      if (process.env.NODE_ENV === "development") {
        console.error("Error saving calendar item:", error);
      }
      // Error toast already shown by hooks (useCreateCalendarItem, useUpdateCalendarItem)
    }
  };

  // Reset form when dialog closes
  const handleDialogClose = () => {
    if (!createLoading && !updateLoading && !deleteLoading) {
      setDialogOpen(false);
      setEditingItemId(null);
      setUpdateScope(UPDATE_SCOPE.THIS_ONLY);
    }
  };

  // Delete calendar item handler (supports event, task, reminder)
  const handleDelete = async () => {
    // HIGH PRIORITY FIX #7: Race condition protection
    if (deleteLoading) {
      return; // Prevent double deletion
    }

    if (!editingItemId) return;

    try {
      // Determine type based on current dialog tab
      let itemType = CALENDAR_ITEM_TYPES.EVENT;
      if (dialogTab === "task") {
        itemType = CALENDAR_ITEM_TYPES.TASK;
      } else if (dialogTab === "reminder") {
        itemType = CALENDAR_ITEM_TYPES.REMINDER;
      }

      // Build delete payload with event_id, type, and delete_on scope
      const deletePayload = {
        event_id: editingItemId,
        type: itemType,
        delete_on: deleteScope,
      };

      const result = await deleteItem(deletePayload);

      if (result.success) {
        // LOW PRIORITY FIX #14: Use extracted reset function
        resetAllFormState();
        setDeleteConfirmOpen(false);
        setEventToDelete(null);
        setDeleteScope(UPDATE_SCOPE.THIS_ONLY);

        // Refresh calendar events for the current visible range
        const calendarEl = calendarRef.current;
        if (calendarEl) {
          try {
            const calendarApi = calendarEl.getApi();
            const view = calendarApi.view;
            if (view && view.activeStart && view.activeEnd) {
              const start = moment(view.activeStart).format("YYYY-MM-DD");
              const end = moment(view.activeEnd).format("YYYY-MM-DD");
              refetchEvents({ startDate: start, endDate: end });
            } else {
              // Fallback to month range
              const start = moment(date).startOf("month").format("YYYY-MM-DD");
              const end = moment(date).endOf("month").format("YYYY-MM-DD");
              refetchEvents({ startDate: start, endDate: end });
            }
          } catch (err) {
            // Fallback to month range if calendar API fails
            const start = moment(date).startOf("month").format("YYYY-MM-DD");
            const end = moment(date).endOf("month").format("YYYY-MM-DD");
            refetchEvents({ startDate: start, endDate: end });
          }
        } else {
          // Fallback to month range
          const start = moment(date).startOf("month").format("YYYY-MM-DD");
          const end = moment(date).endOf("month").format("YYYY-MM-DD");
          refetchEvents({ startDate: start, endDate: end });
        }
      } else {
        // CRITICAL FIX #4: Handle failure case - hooks show toast, but ensure we don't proceed
        // Error message already shown by hook via toast
        if (process.env.NODE_ENV === "development") {
          console.error("Failed to delete calendar item:", result.error);
        }
      }
    } catch (error) {
      // CRITICAL FIX #4: Ensure errors are properly handled
      // Hooks handle toast messages, but log for debugging
      if (process.env.NODE_ENV === "development") {
        console.error("Error deleting calendar item:", error);
      }
      // Error toast already shown by hooks (useDeleteCalendarItem)
    }
  };

  return (
    <>
      <Helmet>
        <title>Dashboard: Calendar Events</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: "background.default",
          minHeight: "100%",
          py: 8,
        }}
      >
        <Container maxWidth={false}>
          <Grid container justifyContent="space-between" spacing={3}>
            <Grid item>
              <Typography color="textPrimary" variant="h5">
                Calendar Events
              </Typography>
              <Breadcrumbs
                aria-label="breadcrumb"
                separator={<ChevronRightIcon fontSize="small" />}
                sx={{ mt: 1 }}
              >
                <Link
                  color="textPrimary"
                  component={RouterLink}
                  to="/"
                  variant="subtitle2"
                >
                  Dashboard
                </Link>
                <Typography color="textSecondary" variant="subtitle2">
                  Calendar Events
                </Typography>
              </Breadcrumbs>
            </Grid>
            <Grid
              item
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
              }}
            >
              <FilterDropdown
                selectedFilterTypes={selectedFilterTypes}
                onFilterChange={handleFilterChange}
                onSelectAll={handleFilterSelectAll}
                onDeselectAll={handleFilterDeselectAll}
              />
            </Grid>
          </Grid>
          <Box sx={{ mt: 3 }}>
            <CalendarToolbar
              date={date}
              onDateNext={handleDateNext}
              onDatePrev={handleDatePrev}
              onDateToday={handleDateToday}
              onViewChange={handleViewChange}
              view={view}
            />
          </Box>
          <Card sx={{ mt: 3, p: 2 }}>
            <FullCalendarWrapper>
              <FullCalendar
                allDayClassNames="projectric-all-day"
                dayMaxEventRows={4}
                eventDisplay="block"
                displayEventTime={false}
                events={calendarEvents}
                headerToolbar={false}
                height={view === "listWeek" ? "auto" : 800}
                initialDate={date}
                initialView={view}
                eventClick={handleEventClick}
                weekends
                plugins={[
                  dayGridPlugin,
                  interactionPlugin,
                  listPlugin,
                  timeGridPlugin,
                  timelinePlugin,
                ]}
                ref={calendarRef}
                rerenderDelay={10}
                selectable={false}
                dateClick={handleDateClick}
              />
            </FullCalendarWrapper>
          </Card>

          <EventFormDialog
            open={dialogOpen}
            onClose={handleDialogClose}
            dialogTab={dialogTab}
            dialogTitle={dialogTitle}
            isEditing={Boolean(editingItemId)}
            onTabChange={handleDialogTabChange}
            onTitleChange={setDialogTitle}
            onSave={handleSave}
            onDelete={() => {
              setDeleteScope(UPDATE_SCOPE.THIS_ONLY); // Reset to default: This event only
              setDeleteConfirmOpen(true);
            }}
            loading={createLoading || updateLoading || deleteLoading}
            updateScope={updateScope}
            onUpdateScopeChange={setUpdateScope}
            eventFormProps={{
              startDate,
              endDate,
              eventStartTime,
              eventEndTime,
              isEventAllDay,
              eventRepeat,
              selectedColor,
              colorAnchorEl,
              notifications,
              location,
              description,
              selectedUsers,
              // Custom recurrence props for summary card
              customRepeatEvery,
              customRepeatUnit,
              customMonthlyPattern,
              customWeeklyDays,
              customEndMode,
              customEndOnDate,
              customEndAfterCount,
              onStartDateChange: handleStartDateChange,
              onEndDateChange: setEndDate,
              onStartTimeChange: setEventStartTime,
              onEndTimeChange: setEventEndTime,
              onAllDayChange: handleAllDayChange,
              onRepeatChange: setEventRepeat,
              onColorMenuOpen: handleColorMenuOpen,
              onColorMenuClose: handleColorMenuClose,
              onColorSelect: handleColorSelect,
              onNotificationChange: handleNotificationChange,
              onRemoveNotification: handleRemoveNotification,
              onAddNotification: handleAddNotification,
              onCustomRecurrenceOpen: () => setCustomRecurrenceOpen(true),
              onLocationChange: setLocation,
              onDescriptionChange: setDescription,
              onUsersChange: setSelectedUsers,
            }}
            taskFormProps={{
              dueDate: startDate,
              onDueDateChange: setStartDate,
              startTime: taskStartTime,
              endTime: taskEndTime,
              onStartTimeChange: setTaskStartTime,
              onEndTimeChange: setTaskEndTime,
              isAllDay: isTaskAllDay,
              onAllDayChange: handleTaskAllDayChange,
              selectedColor: taskSelectedColor,
              colorAnchorEl: taskColorAnchorEl,
              onColorMenuOpen: handleTaskColorMenuOpen,
              onColorMenuClose: handleTaskColorMenuClose,
              onColorSelect: handleTaskColorSelect,
              selectedUsers: taskSelectedUsers,
              onUsersChange: setTaskSelectedUsers,
              description,
              onDescriptionChange: setDescription,
            }}
            reminderFormProps={{
              reminderDate,
              onReminderDateChange: setReminderDate,
              startTime: reminderStartTime,
              endTime: reminderEndTime,
              onStartTimeChange: setReminderStartTime,
              onEndTimeChange: setReminderEndTime,
              isAllDay: isReminderAllDay,
              onAllDayChange: handleReminderAllDayChange,
              selectedColor: reminderSelectedColor,
              colorAnchorEl: reminderColorAnchorEl,
              onColorMenuOpen: handleReminderColorMenuOpen,
              onColorMenuClose: handleReminderColorMenuClose,
              onColorSelect: handleReminderColorSelect,
              selectedUsers: reminderSelectedUsers,
              onUsersChange: setReminderSelectedUsers,
              description,
              onDescriptionChange: setDescription,
            }}
          />

          <CustomRecurrenceDialog
            open={customRecurrenceOpen}
            onClose={() => setCustomRecurrenceOpen(false)}
            selectedDate={startDate}
            repeatEvery={customRepeatEvery}
            repeatUnit={customRepeatUnit}
            monthlyPattern={customMonthlyPattern}
            weeklyDays={customWeeklyDays}
            endMode={customEndMode}
            endOnDate={customEndOnDate}
            endAfterCount={customEndAfterCount}
            onRepeatEveryChange={setCustomRepeatEvery}
            onRepeatUnitChange={setCustomRepeatUnit}
            onMonthlyPatternChange={setCustomMonthlyPattern}
            onWeeklyDaysChange={setCustomWeeklyDays}
            onEndModeChange={setCustomEndMode}
            onEndOnDateChange={setCustomEndOnDate}
            onEndAfterCountChange={setCustomEndAfterCount}
          />

          {/* Delete confirmation dialog */}
          <Dialog
            open={deleteConfirmOpen}
            onClose={() => {
              setDeleteConfirmOpen(false);
              setDeleteScope(UPDATE_SCOPE.THIS_ONLY);
              setEventToDelete(null);
            }}
            aria-labelledby="delete-dialog-title"
            aria-describedby="delete-dialog-description"
            maxWidth="sm"
            fullWidth
          >
            <DialogContent>
              <DialogContentText
                id="delete-dialog-description"
                sx={{
                  pt: 1,
                  mb: 2,
                  color: "#222",
                  lineHeight: "24px",
                  fontWeight: "600",
                }}
              >
                Are you sure you want to delete this {dialogTab}?
              </DialogContentText>

              {/* Delete scope options - only for events (tasks/reminders don't repeat) */}
              {dialogTab === "event" && (
                <FormControl component="fieldset" sx={{ width: "100%", mt: 2 }}>
                  <FormLabel
                    component="legend"
                    sx={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#222",
                      mb: 1,
                    }}
                  >
                    Delete scope:
                  </FormLabel>
                  <RadioGroup
                    value={deleteScope}
                    onChange={(e) => setDeleteScope(Number(e.target.value))}
                    sx={{ mt: 1 }}
                  >
                    <FormControlLabel
                      value={UPDATE_SCOPE.THIS_ONLY}
                      control={<Radio size="small" />}
                      label="This event only"
                    sx={{
                      "& .MuiFormControlLabel-label": {
                        fontSize: "14px",
                        color: "#333",
                      },
                    }}
                  />
                    <FormControlLabel
                      value={UPDATE_SCOPE.THIS_AND_FOLLOWING}
                      control={<Radio size="small" />}
                      label="This and following events"
                    sx={{
                      "& .MuiFormControlLabel-label": {
                        fontSize: "14px",
                        color: "#333",
                      },
                    }}
                  />
                    <FormControlLabel
                      value={UPDATE_SCOPE.ALL}
                      control={<Radio size="small" />}
                      label="All events in group"
                    sx={{
                      "& .MuiFormControlLabel-label": {
                        fontSize: "14px",
                        color: "#333",
                      },
                    }}
                  />
                </RadioGroup>
              </FormControl>
              )}

              <DialogContentText
                sx={{
                  fontSize: "12px",
                  color: "#666",
                  fontStyle: "italic",
                  mt: 2,
                }}
              >
                This action cannot be undone.
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ pb: 2, px: 3 }}>
              <Button
                onClick={() => {
                  setDeleteConfirmOpen(false);
                  setDeleteScope(UPDATE_SCOPE.THIS_ONLY);
                  setEventToDelete(null);
                }}
                color="primary"
                disabled={deleteLoading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDelete}
                color="error"
                variant="contained"
                disabled={deleteLoading}
                autoFocus
              >
                {deleteLoading ? "Deleting..." : "Delete"}
              </Button>
            </DialogActions>
          </Dialog>
        </Container>
      </Box>
    </>
  );
};

export default CalendarEvents;
